import { StrictFetcher } from '@this/src/util';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import _ from 'lodash';

import type { AsyncWorker } from '@this/src/lib/async_queue';
import { AsyncQueue, asyncWorkers } from '@this/src/lib/async_queue';
import Hotel from '@this/domain/hotel/hotel';
import MarginType from '@this/src/domain/organization/margin_type2';
import type User from '@this/domain/user/user';
import { reportError } from '@this/lib/bugsnag';
import HotelCandidateBox from '../../hotel_box/hotel_candidate_box';
import type { HotelResponse } from '../../hotel_box/hotel_candidate_box';
import type HotelList from '../../../../../domain/hotel/hotel_list';

interface HotelListProps {
  destLocation?: { lat: number; lng: number } | null;
  me: User | null;
  onChange: () => void;
  hotels: HotelList;
  hotelPriceLimit: number | null;
  searchQueryId: number | null;
  user: any;
  intlSpCandidatePrice?: boolean;
}

const SelectHotelList: React.FC<HotelListProps> = ({
  destLocation,
  me,
  onChange,
  hotels,
  hotelPriceLimit,
  searchQueryId,
  user,
  intlSpCandidatePrice = false
}) => {
  const [queue] = useState(() => new AsyncQueue(20));
  const [workers, setWorkers] = useState<AsyncWorker[]>([]);
  const workersRef = useRef<AsyncWorker[]>([]);
  const isFetchingRef = useRef(false);
  const previousSearchQueryIdRef = useRef<number | null>(searchQueryId);

  // workersRefを最新の状態に同期
  useEffect(() => {
    workersRef.current = workers;
  }, [workers]);

  const handleSelect = useCallback(
    (hotel: Hotel) => {
      onChange();
      if (hotel.id) hotels.selectWithAnimation(hotel.id);
    },
    [onChange, hotels]
  );

  const fetchHotelPrices = useCallback(() => {
    // StaticFile利用時のみ、一括で価格の更新を行いたい
    // https://aitravel.atlassian.net/browse/AITRAVEL-3437
    if (!hotels.filteredList.some(h => h.operator_class_name === 'RakutenStaticfileOperator')) return;

    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const newWorkers = asyncWorkers(queue);
    setWorkers(newWorkers);

    const rakutenStaticHotels = hotels.filteredList
      .filter(h => h.operator_class_name === 'RakutenStaticfileOperator')
      .sort((a, b) => (a.orderedIndex ?? Infinity) - (b.orderedIndex ?? Infinity))
      .slice(0, 20);

    rakutenStaticHotels.forEach(hotel => {
      hotel.setHotelLoading(true);

      queue.enqueue(next => {
        StrictFetcher.get<HotelResponse>(
          `/hotels/${hotel.id}/rakuten_static_hotel_price/?search_query_id=${searchQueryId}`,
          'hotels/show.json'
        ).then(
          result => {
            if (result.hotel.sold_out) {
              hotel.setSoldOut(true);
            } else if (result.hotel.too_late) {
              hotel.setTooLate(true);
            } else {
              let marginType: MarginType | undefined;
              if (result.margin_type) {
                marginType = new MarginType(result.margin_type);
              }
              const includeTax = hotels.includeTax;
              const updatedHotel = new Hotel(
                _.merge(result.hotel, {
                  includeTax,
                  showFee: result.show_fee,
                  marginType,
                  walkminute: hotel.walkminute,
                  loading: false
                })
              );

              if (
                (result.is_domestic && !result.user.show_hotel_of_over_limit) ||
                (!result.is_domestic && !result.user.show_foreign_hotel_of_over_limit)
              ) {
                // 規定金額超過ホテル表示フラグがオフの場合
                const limit = hotelPriceLimit || 0;
                const isOverLimit = limit > 0 && updatedHotel.getAveragePrice()! > limit;
                updatedHotel.setIsOverLimit(isOverLimit);
              }

              hotels.updateHotelList(updatedHotel);
            }

            hotel.setHotelLoading(false);
            next();
          },
          e => {
            hotel.setHotelLoading(false);
            next();
          }
        );
      });
    });

    queue.enqueue(() => {
      workersRef.current.forEach(worker => {
        worker.isContinue = false;
      });
      setWorkers([]);
      isFetchingRef.current = false;
    });
  }, [hotels, searchQueryId, hotelPriceLimit, queue]);

  // componentDidMount相当
  useEffect(() => {
    const hotelSortKey = localStorage.getItem('hotelSortKey');
    if (hotelSortKey) hotels.sortBy(hotelSortKey);
    fetchHotelPrices();
  }, [hotels, fetchHotelPrices]);

  // componentDidUpdate相当（searchQueryId変更時）
  useEffect(() => {
    if (previousSearchQueryIdRef.current !== searchQueryId) {
      isFetchingRef.current = false;
      previousSearchQueryIdRef.current = searchQueryId;
      fetchHotelPrices();
    }
  }, [searchQueryId, fetchHotelPrices]);

  // クリーンアップ処理
  useEffect(() => {
    return () => {
      // コンポーネントアンマウント時にworkersを停止
      workersRef.current.forEach(worker => {
        worker.isContinue = false;
      });
      isFetchingRef.current = false;
    };
  }, []);

  const renderedHotels = useMemo(() => {
    try {
      return hotels.filteredList.map(h => (
        <React.Fragment key={h.id ?? Math.random()}>
          <HotelCandidateBox
            destLocation={destLocation}
            me={me}
            user={user}
            hotel={h}
            hotels={hotels}
            inList
            hotelPriceLimit={hotelPriceLimit}
            searchQueryId={searchQueryId}
            selected={hotels.currentId === h.id}
            handleSelect={handleSelect}
            intlSpCandidatePrice={intlSpCandidatePrice}
          />
        </React.Fragment>
      ));
    } catch (e) {
      reportError(e);
      return null;
    }
  }, [
    hotels.filteredList,
    hotels.currentId,
    destLocation,
    me,
    user,
    hotelPriceLimit,
    searchQueryId,
    handleSelect,
    intlSpCandidatePrice
  ]);

  if (renderedHotels === null) {
    return null;
  }

  return <div style={{ display: 'block' }}>{renderedHotels}</div>;
};

export default SelectHotelList;
